<template>
  <div class="tx-ktgdealersearch plugin-dealersearch-list" :class="{ '-fix-map': fixMap }" :key="componentKey">
    <div class="dealersearch-list__header" ref="header">
      <div class="header-wrapper -max-width-limited">
        <SearchForm v-if="mobile" />
        <FilterBar />
        <FilterSelected v-if="initialized && mobile" />
      </div>
    </div>
    <div class="dealersearch-list__content">
      <div class="content-wrapper -max-width-limited">
        <div class="content-col content-col-one">
          <SearchForm v-if="!mobile" />
          <SearchResults
              :style="{ transform: fixMap && contentTransform ? 'translateY(' + contentTransform + 'px)' : '' }"
              @mouseenter-result="hoveredPoiIndex = $event"
              @mouseleave-result="hoveredPoiIndex = -1"/>
        </div>
        <div class="content-col content-col-two" :style="{ height: mapHeight ? mapHeight + 'px' : '' }">
          <GoogleMap
              v-if="consentIsGiven()"
              :hoveredPoiIndex="hoveredPoiIndex"
              @dragstart="handleFixMapButton"/>
          <Placeholder
            v-if="!consentIsGiven()"
            @giveConsent="forceRerender"/>
        </div>
      </div>
    </div>
    <div class="dealersearch-list__fix-map" v-if="mobile && showFixMapButton && !fixMap">
      <div class="fix-map__button btn -btn-alpha" @click="handleFixMapButton">
        <span>{{ $t('map.control.fix') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { mapState, mapActions } from 'vuex'
import FilterBar from '@/components/FilterBar'
import FilterSelected from '@/components/FilterSelected'
import SearchForm from '@/components/SearchForm'
import SearchResults from '@/components/SearchResults'
import GoogleMap from '@/components/GoogleMap'
import Placeholder from '@/components/Placeholder'

export default {
  name: 'App',
  components: {
    FilterBar,
    FilterSelected,
    SearchForm,
    SearchResults,
    GoogleMap,
    Placeholder
  },
  provide () {
    return {
      mobile: computed(() => this.mobile),
      phone: computed(() => this.phone)
    }
  },
  data () {
    return {
      mobile: false,
      phone: false,
      hoveredPoiIndex: -1,
      fixMap: false,
      showFixMapButton: false,
      disableScroll: false,
      scrollY: 0,
      scrollDiv: 0,
      mapHeight: '',
      contentTransform: '',
      componentKey: 0
    }
  },
  computed: {
    ...mapState({
      initialized: state => state.initialized
    })
  },
  methods: {
    ...mapActions([
      'init'
    ]),
    handleResize () {
      this.mobile = window.innerWidth < 1024
      if (this.mobile) {
        const screenSize = window.innerHeight
        this.mapHeight = screenSize - this.$refs.header.getBoundingClientRect().height - 100
        this.contentTransform = this.mapHeight - (screenSize / 2)
      } else {
        this.mapHeight = ''
        this.contentTransform = ''
      }
    },
    handleScroll () {
      this.showFixMapButton = window.scrollY > 100
      if (this.fixMap && this.scrollDiv !== 0) {
        this.contentTransform = 0
        this.fixMap = false
      }
      this.scrollDiv = this.scrollY - window.scrollY
      this.scrollY = window.scrollY
    },
    handleFixMapButton () {
      this.fixMap = true
      this.scrollDiv = 0
      this.scrollY = 0
      window.scrollTo(0, 0)
    },
    forceRerender: function () {
      this.componentKey += 1
    },
    consentIsGiven: function () {
      if (window.sessionStorage.length > 0) {
        if (window.sessionStorage.googlemapsConsent === 'consentGiven') {
          return true
        }
      }
      return false
    }
  },
  created () {
    // Provide global variables
    this.phone = /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent)

    // Set filter params from querystring
    const url = new URL(window.location.href)
    if (url.searchParams.has('location-name') && url.searchParams.has('lat') && url.searchParams.has('lng')) {
      this.$store.commit('filter/setLocation', url.searchParams.get('location-name'))
      this.$store.commit('filter/setLatitude', url.searchParams.get('lat'))
      this.$store.commit('filter/setLongitude', url.searchParams.get('lng'))
    } else if (url.searchParams.has('dealername')) {
      this.$store.commit('filter/setDealername', url.searchParams.get('dealername'))
    } else if (url.searchParams.has('latitudeA') && url.searchParams.has('longitudeA') && url.searchParams.has('latitudeB') && url.searchParams.has('longitudeB')) {
      this.$store.commit('filter/setSwLatitude', url.searchParams.get('latitudeA'))
      this.$store.commit('filter/setSwLongitude', url.searchParams.get('longitudeA'))
      this.$store.commit('filter/setNeLatitude', url.searchParams.get('latitudeB'))
      this.$store.commit('filter/setNeLongitude', url.searchParams.get('longitudeB'))
    } else if (window.locale) {
      if (window.locale.includes('-')) {
        this.$store.commit('filter/setCountry', window.locale.split('-')[1])
      } else {
        this.$store.commit('filter/setCountry', window.locale.split('_')[1])
      }
    }
    if (url.searchParams.has('services')) {
      this.$store.commit('filter/setServices', url.searchParams.get('services').split(',').map(service => parseInt(service)))
    }
    if (url.searchParams.has('models')) {
      this.$store.commit('filter/setModels', url.searchParams.get('models').split(';'))
    }
    if (url.searchParams.has('divisions')) {
      this.$store.commit('filter/setDivisions', url.searchParams.get('divisions').split(','))
    }
    if (url.searchParams.has('vehicle-model')) {
      this.$store.commit('filter/setModel', url.searchParams.get('vehicle-model'))
    }
    if (url.searchParams.has('vehicle-layout')) {
      this.$store.commit('filter/setLayouts', url.searchParams.get('vehicle-layout').split(','))
    }
    if (url.searchParams.has('country')) {
      this.$store.commit('filter/setCountry', url.searchParams.get('country'))
    }

    // Load initial data
    this.init()
  },
  mounted () {
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    this.handleScroll()
    window.addEventListener('scroll', this.handleScroll)
  },
  updated () {
    this.handleResize()
    this.handleScroll()
  }
}
</script>
